import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  userAffiliationTypeService,
  AffiliationCodeInvalidError,
  AffiliationTypeReferencedError,
  type UserAffiliationTypeListParams,
  type UserAffiliationTypeListResponse,
  type UserAffiliationTypeCreateRequest,
  type UserAffiliationTypeUpdateRequest,
} from '../../services/api/userAffiliationTypeService';

export const loadUserAffiliationTypes = createAsyncThunk<
  UserAffiliationTypeListResponse,
  UserAffiliationTypeListParams | void
>(
  'userAffiliationType/load',
  async (params, { rejectWithValue }) => {
    try {
      return await userAffiliationTypeService.list(params ?? {});
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load affiliation types');
    }
  }
);

export const createUserAffiliationType = createAsyncThunk<
  { affiliationTypeId: number },
  { request: UserAffiliationTypeCreateRequest }
>(
  'userAffiliationType/create',
  async ({ request }, { rejectWithValue }) => {
    try {
      return await userAffiliationTypeService.create(request);
    } catch (error: unknown) {
      if (error instanceof AffiliationCodeInvalidError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create affiliation type');
    }
  }
);

export const updateUserAffiliationType = createAsyncThunk<
  { affiliationTypeId: number },
  { id: number; request: UserAffiliationTypeUpdateRequest }
>(
  'userAffiliationType/update',
  async ({ id, request }, { rejectWithValue }) => {
    try {
      return await userAffiliationTypeService.update(id, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update affiliation type');
    }
  }
);

export const deleteUserAffiliationType = createAsyncThunk<
  { affiliationTypeId: number; dependentUserCount: number },
  { id: number }
>(
  'userAffiliationType/delete',
  async ({ id }, { rejectWithValue }) => {
    try {
      return await userAffiliationTypeService.delete(id);
    } catch (error: unknown) {
      if (error instanceof AffiliationTypeReferencedError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete affiliation type');
    }
  }
);
